import type {MouseEvent, ReactNode} from 'react';
import type {TooltipContentProps as RadixTooltipContentProps} from '@radix-ui/react-tooltip';
import {CircleQuestionMark} from 'lucide-react';
import {cn} from '../lib/cn';
import {TooltipContent, TooltipProvider, TooltipRoot, TooltipTrigger} from '../tooltip';

export interface HelpTooltipProps {
  content?: ReactNode;
  side?: RadixTooltipContentProps['side'];
  variant?: 'light' | 'dark';
  onClick?: (e: MouseEvent) => void;
  className?: string;
  contentClassName?: string;
  size?: 16 | 20;
  label?: string;
  triggerAs?: 'button' | 'span';
}

export function HelpTooltip({
  content,
  side,
  variant,
  onClick,
  className,
  contentClassName,
  size = 16,
  label = 'More info',
  triggerAs = 'button',
}: HelpTooltipProps) {
  const triggerClassName = cn(
    'relative flex shrink-0 cursor-help items-center justify-center rounded-full',
    'text-[var(--chekin-color-gray-2)] outline-none transition-colors hover:text-[var(--chekin-color-brand-blue)]',
    triggerAs === 'button' && 'focus-visible:shadow-[var(--chekin-shadow-focus)]',
    className,
  );
  const triggerStyle = {width: size, height: size};
  const triggerContent = (
    <>
      <CircleQuestionMark aria-hidden="true" style={{width: size, height: size}} />
      <span className="absolute -left-0.5 -top-0.5 h-5 w-5" />
    </>
  );

  return (
    <TooltipProvider>
      <TooltipRoot>
        <TooltipTrigger asChild onClick={onClick}>
          {triggerAs === 'button' ? (
            <button
              type="button"
              aria-label={label}
              className={triggerClassName}
              style={triggerStyle}
            >
              {triggerContent}
            </button>
          ) : (
            <span aria-label={label} className={triggerClassName} style={triggerStyle}>
              {triggerContent}
            </span>
          )}
        </TooltipTrigger>
        <TooltipContent
          variant={variant}
          side={side}
          className={cn('max-w-64', contentClassName)}
        >
          {content}
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  );
}
